import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "../../../../../query/keys";
import { useModerationPageQueries } from "../../../../../query/hooks/useModerationPageQueries";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Grid,
  Divider,
  CircularProgress,
  Tabs,
  Tab,
} from "@material-ui/core";
import { Visibility, Person, Refresh } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { Link } from "react-router-dom";
import { showToast } from "../../../../../utils/toast";
import {
  reviewFlaggedContentAPI,
  banUserAPI,
  bulkReviewFlaggedContentAPI,
  getContentPreviewAPI,
  updatePostReportStatusAPI,
  updateAppealStatusAPI,
  updatePostStatusAPI,
  updateProfileReportStatusAPI,
  updateCommentReportStatusAPI,
  warningUserAPI,
  unbanUserAPI,
} from "../../../../../api/moderationApi";
import { getFileByIdAPI } from "../../../../../api/userApi";
import defaultAvatars from "../../../../../assets/defaultAvatars.svg";
import ReportPostCard from "./ReportPostCard";
import ReportProfileCard from "./ReportProfileCard";
import AppealCard from "./AppealCard";
import ReportCommentCard from "./ReportCommentCard";
import { useFilteredItems } from "../../../../../hooks/useFilteredItems";
import { ReportFilters } from "./ReportFilters";
import { ReportCustomFilters } from "./ReportCustomFilters";

export const useStyles = makeStyles(theme => ({
  container: {
    padding: theme.spacing(3),
    backgroundColor: "#0f0f0f",
    minHeight: "100vh",
    color: "#fff",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(3),
  },
  statsGrid: {
    marginBottom: theme.spacing(3),
  },
  statCard: {
    backgroundColor: "#1a1a1a",
    border: "1px solid #333",
    color: "#fff",
  },
  statValue: {
    fontSize: "2rem",
    fontWeight: "bold",
    color: "#FE5A97",
  },
  filtersCard: {
    backgroundColor: "#1a1a1a",
    border: "1px solid #333",
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
  },
  contentCard: {
    backgroundColor: "#1a1a1a",
    border: "1px solid #333",
    marginBottom: theme.spacing(2),
    color: "#fff",
    "&:hover": {
      borderColor: "#FE5A97",
    },
  },
  contentHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(1),
  },
  userInfo: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  contentPreview: {
    maxWidth: "200px",
    maxHeight: "200px",
    borderRadius: "8px",
    objectFit: "cover",
  },
  contentPreviewContainer: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: "#2a2a2a",
    borderRadius: "8px",
    border: "1px solid #444",
  },
  contentPreviewImage: {
    maxWidth: "100%",
    maxHeight: "400px",
    borderRadius: "8px",
    objectFit: "contain",
    marginBottom: theme.spacing(1),
  },
  contentPreviewVideo: {
    maxWidth: "100%",
    maxHeight: "400px",
    borderRadius: "8px",
    marginBottom: theme.spacing(1),
  },
  contentPreviewText: {
    padding: theme.spacing(1.5),
    backgroundColor: "#1a1a1a",
    borderRadius: "4px",
    border: "1px solid #333",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  expandButton: {
    marginTop: theme.spacing(1),
    color: "#FE5A97",
    textTransform: "none",
  },
  actionButtons: {
    display: "flex",
    gap: theme.spacing(1),
  },
  approveButton: {
    backgroundColor: "#4caf50",
    color: "#fff",
    "&:hover": {
      backgroundColor: "#45a049",
    },
  },
  rejectButton: {
    backgroundColor: "#f44336",
    color: "#fff",
    "&:hover": {
      backgroundColor: "#da190b",
    },
  },
  escalateButton: {
    backgroundColor: "#ff9800",
    color: "#fff",
    "&:hover": {
      backgroundColor: "#e68900",
    },
  },
  banButton: {
    backgroundColor: "#d32f2f",
    color: "#fff",
    "&:hover": {
      backgroundColor: "#b71c1c",
    },
  },
  reviewDialog: {
    "& .MuiDialog-paper": {
      backgroundColor: "#1a1a1a",
      color: "#fff",
      border: "1px solid #333",
    },
  },
  severityHigh: {
    backgroundColor: "#f44336",
    color: "#fff",
  },
  severityMedium: {
    backgroundColor: "#ff9800",
    color: "#fff",
  },
  severityLow: {
    backgroundColor: "#4caf50",
    color: "#fff",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "200px",
  },
  bulkActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: "#1a1a1a",
    border: "1px solid #333",
    borderRadius: "8px",
    color: "#fff",
  },
  tabsContainer: {
    marginBottom: theme.spacing(3),
    borderBottom: "1px solid #333",
    overflowX: "auto",
  },
  tab: {
    color: "#999",
    textTransform: "none",
    fontWeight: 500,
    "&.Mui-selected": {
      color: "#FE5A97",
    },
  },
  tabIndicator: {
    backgroundColor: "#FE5A97",
  },
  postStatusBadge: {
    marginLeft: theme.spacing(1),
  },
  modalActions: {
    backgroundColor: "#131313",
    padding: "16px 24px",
    justifyContent: "space-between",
    borderRadius: "0 0 12px 12px",
  },
  cancelButton: {
    color: "#fff",
    padding: "12px 40px",
    maxWidth: "142px",
    fontSize: "16px",
    borderRadius: "6px",
    border: "1px solid #FE5A97",
    textTransform: "capitalize",
    backgroundColor: "transparent",
    "&:hover": {
      backgroundColor: "#FE5A97",
    },
    [theme.breakpoints.down("sm")]: {
      maxWidth: "100px",
      fontSize: "14px",
    },
  },
  sendButton: {
    color: "#fff",
    padding: "12px 40px",
    maxWidth: "240px",
    fontSize: "16px",
    borderRadius: "6px",
    textTransform: "capitalize",
    backgroundColor: "#FE5A97",
    textWrap: "nowrap",
    "&:hover": {
      border: "1px solid #FE5A97",
      backgroundColor: "transparent",
    },
    [theme.breakpoints.down("sm")]: {
      maxWidth: "180px",
      fontSize: "14px",
      padding: "12px 24px",
    },
  },
}));

interface FlaggedContent {
  _id: string;
  contentId: string;
  contentType:
  | "post"
  | "message"
  | "comment"
  | "video"
  | "dm"
  | "profile_description";
  flaggedText: string;
  matchedTerms: Array<{
    _id: string;
    term: string;
    category: string;
    type: string;
    pattern?: string;
  }>;
  user: {
    _id: string;
    username: string;
    firstName?: string;
    lastName?: string;
    profileImg?: string;
    email: string;
    warningsCount?: number
  };
  reason: string;
  severity: "low" | "medium" | "high";
  status: "pending" | "approved" | "rejected" | "escalated";
  createdAt: string;
  reviewedBy?: string;
  reviewedAt?: string;
  reviewNotes?: string;
}

interface ModerationStats {
  totalFlags: number;
  pendingFlags: number;
  approvedFlags: number;
  rejectedFlags: number;
  escalatedFlags: number;
  bannedUsers: number;
  highRiskUsers: number;
}

export interface AiFlag {
  _id: string;
  field: string;
  severity: "low" | "medium" | "high";
  provider: string;
  originalText: string;
  matchedTerms: string[];
}

export interface PostReport {
  _id: string;
  postId: string;
  userId: string;
  reason: string;
  comment?: string;
  status: "open" | "pending" | "reviewing" | "resolved" | "postDeleted";
  createdAt: string;
  reviewedBy?: string;
  reviewedAt?: string;
  aiFlags?: AiFlag[];
  reporter?: {
    _id: string;
    username: string;
    firstName?: string;
    lastName?: string;
    profileImg?: string;
    email: string;
  };
  postOwner?: {
    _id: string;
    username: string;
    firstName?: string;
    lastName?: string;
    profileImg?: string;
    email: string;
  };
  post?: {
    _id: string;
    description?: string;
    userId: string;
    status?: string;
    content?: Array<{
      _id: string;
      type: string;
      mediaUrl: string;
      paidContent?: boolean;
    }>;
    type?: string;
    price?: number;
    hashtags?: string[];
  };
  user?: {
    _id: string;
    username: string;
    firstName?: string;
    lastName?: string;
    profileImg?: string;
    email: string;
  };
}
export interface CommentReport {
  _id: string;
  commentId: string;
  userId: string;
  reason: string;
  commentText?: string;
  status: "open" | "pending" | "reviewing" | "resolved" | "commentDeleted";
  createdAt: string;
  reviewedBy?: string;
  reviewedAt?: string;
  aiFlags?: AiFlag[];
  reporter?: {
    _id: string;
    username: string;
    firstName?: string;
    lastName?: string;
    profileImg?: string;
    email: string;
  };
  commentOwner?: {
    _id: string;
    username: string;
    firstName?: string;
    lastName?: string;
    profileImg?: string;
    email: string;
  };
  comment?: {
    _id: string;
    comment?: string;
    userId: string;
    status?: string;
    content?: Array<{
      _id: string;
      type: string;
      mediaUrl: string;
      paidContent?: boolean;
    }>;
    type?: string;
    price?: number;
    hashtags?: string[];
  };
  user?: {
    _id: string;
    username: string;
    firstName?: string;
    lastName?: string;
    profileImg?: string;
    email: string;
  };
  commentDetails: {
    _id: string;
    galleryPostId: string
  }
}

export interface ProfileReport {
  _id: string;
  userId: string; // кого репортять
  reporterId: string; // хто репортить
  reason:
  | "It violates intellectual property"
  | "This author is impersonating someone else"
  | "Self-injury or disorders promotion"
  | "Spam or fraud"
  | "Selling or promoting restricted items";
  comment?: string;
  status: "open" | "pending" | "reviewing" | "resolved" | "userDeleted";
  createdAt: string;
  updatedAt: string;
  aiFlags?: AiFlag[];
  reporter?: {
    _id: string;
    username: string;
    firstName?: string;
    lastName?: string;
    profileImg?: string;
    email: string;
  };

  profile?: {
    _id: string;
    username: string;
    firstName?: string;
    lastName?: string;
    profileImg?: string;
    email: string;
    createdAt?: string;
    updatedAt?: string;
  };
}

export interface Appeal {
  _id: string;
  targetId: string; // універсальний ID (postId або userId)
  targetType: "post" | "profile" | "comment";
  userId: string; // хто подав апеляцію
  commentText: string;
  status: "pending" | "reviewing" | "approved" | "rejected";
  createdAt: string;
  reviewedBy?: string;
  reviewedAt?: string;
  reviewNotes?: string;

  // якщо апеляція на пост
  post?: {
    _id: string;
    description?: string;
    userId: string;
    status?: string;
    thumbnailUrl?: string;
    caption?: string;
  };

  // якщо апеляція на профіль
  profile?: {
    _id: string;
    username: string;
    firstName?: string;
    lastName?: string;
    profileImg?: string;
    profileBannerImage?: string;
    email?: string;
  };

  comment?: {
    _id: string;
    username: string;
    firstName?: string;
    lastName?: string;
    profileImg?: string;
    profileBannerImage?: string;
    email?: string;
  };

  // користувач, який подав апеляцію
  user?: {
    _id: string;
    username: string;
    firstName?: string;
    lastName?: string;
    profileImg?: string;
    email: string;
  };

  // адмін, який розглядав
  reviewer?: {
    _id: string;
    username: string;
    firstName?: string;
    lastName?: string;
  };
}

export interface ReportFiltersType {
  status: string;
  postId: string;
  profileId: string;
  appealId: string;
  commentId: string;
}

export interface AppealFilters {
  status: string;
  targetId: string;
  userId: string;
  sortOrder: "newest" | "oldest";
  dateRange: "all" | "24h" | "7d" | "30d";
}

interface FlaggedFilters {
  status: string;
  severity: string;
  contentType: string;
  sortOrder: "newest" | "oldest";
  dateRange: "all" | "24h" | "7d" | "30d";
}

const FlaggedContentPage: React.FC = () => {
  const classes = useStyles();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState(0);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [avatarUrls, setAvatarUrls] = useState<{ [key: string]: string }>({});
  const [loadingAvatars, setLoadingAvatars] = useState<{
    [key: string]: boolean;
  }>({});
  const [contentPreviews, setContentPreviews] = useState<{
    [key: string]: any;
  }>({});
  const [loadingPreviews, setLoadingPreviews] = useState<{
    [key: string]: boolean;
  }>({});
  const [expandedPreviews, setExpandedPreviews] = useState<{
    [key: string]: boolean;
  }>({});
  const [expandedPostReports, setExpandedPostReports] = useState<{
    [key: string]: boolean;
  }>({});
  const [mediaUrls, setMediaUrls] = useState<{ [key: string]: string }>({});
  const [loadingMedia, setLoadingMedia] = useState<{ [key: string]: boolean }>(
    {}
  );
  const [reviewDialog, setReviewDialog] = useState<{
    open: boolean;
    content: FlaggedContent | null;
    decision: string;
    reason: string;
  }>({
    open: false,
    content: null,
    decision: "",
    reason: "",
  });
  const [reviewContentPreview, setReviewContentPreview] = useState<any>(null);
  const [loadingReviewPreview, setLoadingReviewPreview] = useState(false);
  const [banDialog, setBanDialog] = useState<{
    open: boolean;
    user: any;
    reason: string;
    duration: string;
  }>({
    open: false,
    user: null,
    reason: "",
    duration: "",
  });
  const [warningDialog, setWarningDialog] = useState<{
    open: boolean;
    user: any;
    message: string;
  }>({
    open: false,
    user: null,
    message: "",
  });
  const [reportStatusDialog, setReportStatusDialog] = useState<{
    open: boolean;
    report: PostReport | ProfileReport | CommentReport | null;
    status: string;
    moderatorComment?: string;
    reportType?: "post" | "profile" | "comment" | null;
    duration?: string | null;
  }>({
    open: false,
    report: null,
    status: "",
    moderatorComment: "",
    reportType: "post"
  });

  const [appealStatusDialog, setAppealStatusDialog] = useState<{
    open: boolean;
    appeal: Appeal | null;
    status: string;
    reviewNotes: string;
  }>({
    open: false,
    appeal: null,
    status: "",
    reviewNotes: "",
  });
  const [postStatusDialog, setPostStatusDialog] = useState<{
    open: boolean;
    postId: string | null;
    status: string;
    reason: string;
  }>({
    open: false,
    postId: null,
    status: "",
    reason: "",
  });

  const [filters, setFilters] = useState<FlaggedFilters>({
    status: "pending",
    severity: "",
    contentType: "",
    sortOrder: "oldest",
    dateRange: "all"
  });
  const [reportFilters, setReportFilters] = useState<ReportFiltersType>({
    status: "open",
    postId: "",
    profileId: "",
    appealId: "",
    commentId: ""
  });

  const [appealFilters, setAppealFilters] = useState<AppealFilters>({
    status: "pending",
    targetId: "",
    userId: "",
    sortOrder: "oldest",
    dateRange: "all"
  });

  const [sortOrder, setSortOrder] = useState<"newest" | "oldest">("newest");
  const [dateRange, setDateRange] = useState<"all" | "24h" | "7d" | "30d">("all");

  const reportTypeStatus = reportStatusDialog.reportType === "post" ? "postDeleted" : reportStatusDialog.reportType === "profile" ? "userDeleted" : "commentDeleted";

  const modQueries = useModerationPageQueries(
    activeTab,
    filters,
    reportFilters,
    appealFilters
  );
  const flaggedContent = {
    ...modQueries.flaggedContent,
    items: modQueries.flaggedContent.items as FlaggedContent[],
  };
  const postReports = {
    ...modQueries.postReports,
    items: modQueries.postReports.items as PostReport[],
  };
  const profileReports = {
    ...modQueries.profileReports,
    items: modQueries.profileReports.items as ProfileReport[],
  };
  const commentReports = {
    ...modQueries.commentReports,
    items: modQueries.commentReports.items as CommentReport[],
  };
  const appeals = {
    ...modQueries.appeals,
    items: modQueries.appeals.items as Appeal[],
  };
  const stats = modQueries.stats as ModerationStats | null;
  const { loading, flaggedTabQuery, postReportsQuery, profileReportsQuery, commentReportsQuery, appealsQuery } =
    modQueries;

  const visibleFlaggedContent = useFilteredItems<FlaggedContent>(flaggedContent.items, {
    dateRange: filters.dateRange,
    sortOrder: filters.sortOrder,
  });
  const visiblePostReports = useFilteredItems<PostReport>(postReports.items, {
    dateRange: dateRange,
    sortOrder: sortOrder,
  });
  const visibleProfileReports = useFilteredItems<ProfileReport>(profileReports.items, {
    dateRange: dateRange,
    sortOrder: sortOrder,
  });
  const visibleCommentReports = useFilteredItems<CommentReport>(commentReports.items, {
    dateRange: dateRange,
    sortOrder: sortOrder,
  });
  const visibleAppeals = useFilteredItems<Appeal>(appeals.items, {
    dateRange: appealFilters.dateRange,
    sortOrder: appealFilters.sortOrder,
  });

  const invalidateModeration = useCallback(() => {
    return queryClient.invalidateQueries({ queryKey: queryKeys.moderation.all });
  }, [queryClient]);

  useEffect(() => {
    if (activeTab !== 0 || !flaggedTabQuery.isError) return;
    showToast("Error loading moderation data", "error");
  }, [activeTab, flaggedTabQuery.isError]);

  useEffect(() => {
    if (activeTab !== 1 || !postReportsQuery.isError) return;
    showToast("Error loading post reports", "error");
  }, [activeTab, postReportsQuery.isError]);

  useEffect(() => {
    if (activeTab !== 2 || !profileReportsQuery.isError) return;
    showToast("Error loading profile reports", "error");
  }, [activeTab, profileReportsQuery.isError]);

  useEffect(() => {
    if (activeTab !== 3 || !commentReportsQuery.isError) return;
    showToast("Error loading comment reports", "error");
  }, [activeTab, commentReportsQuery.isError]);

  useEffect(() => {
    if (activeTab !== 4 || !appealsQuery.isError) return;
    showToast("Error loading appeals", "error");
  }, [activeTab, appealsQuery.isError]);

  useEffect(() => {
    if (activeTab !== 0) return;
    flaggedContent.items.forEach((item: FlaggedContent) => {
      if (item.user?._id && item.user?.profileImg) {
        void fetchAvatarUrl(item.user._id, item.user.profileImg);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- avatar fetch on list identity
  }, [activeTab, flaggedContent.items]);

  useEffect(() => {
    if (activeTab !== 1) return;
    postReports.items.forEach((item: PostReport) => {
      if (item.postOwner?._id && item.postOwner?.profileImg) {
        void fetchAvatarUrl(item.postOwner._id, item.postOwner.profileImg);
      }
      if (item.reporter?._id && item.reporter?.profileImg) {
        void fetchAvatarUrl(item.reporter._id, item.reporter.profileImg);
      }
      if (item.post?.content) {
        item.post.content.forEach((contentItem, index) => {
          if (
            contentItem.mediaUrl &&
            contentItem.mediaUrl.startsWith("/api/files/")
          ) {
            const mediaKey = `post_${item.postId}_${index}`;
            if (!mediaUrls[mediaKey] && !loadingMedia[mediaKey]) {
              void fetchMediaUrl(contentItem.mediaUrl, mediaKey);
            }
          }
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, postReports.items]);

  useEffect(() => {
    if (activeTab !== 2) return;
    profileReports.items.forEach((item: ProfileReport) => {
      if (item.profile?._id && item.profile?.profileImg) {
        void fetchAvatarUrl(item.profile._id, item.profile.profileImg);
      }
      if (item.reporter?._id && item.reporter?.profileImg) {
        void fetchAvatarUrl(item.reporter._id, item.reporter.profileImg);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, profileReports.items]);

  useEffect(() => {
    if (activeTab !== 3) return;
    commentReports.items.forEach((item: PostReport) => {
      if (item.postOwner?._id && item.postOwner?.profileImg) {
        void fetchAvatarUrl(item.postOwner._id, item.postOwner.profileImg);
      }
      if (item.reporter?._id && item.reporter?.profileImg) {
        void fetchAvatarUrl(item.reporter._id, item.reporter.profileImg);
      }
      if (item.post?.content) {
        item.post.content.forEach((contentItem, index) => {
          if (
            contentItem.mediaUrl &&
            contentItem.mediaUrl.startsWith("/api/files/")
          ) {
            const mediaKey = `post_${item.postId}_${index}`;
            if (!mediaUrls[mediaKey] && !loadingMedia[mediaKey]) {
              void fetchMediaUrl(contentItem.mediaUrl, mediaKey);
            }
          }
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, profileReports.items]);

  useEffect(() => {
    if (activeTab !== 4) return;
    appeals.items.forEach((item: Appeal) => {
      if (item.user?._id && item.user?.profileImg) {
        void fetchAvatarUrl(item.user._id, item.user.profileImg);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, appeals.items]);

  const fetchAvatarUrl = async (userId: string, profileImg?: string) => {
    if (!profileImg) return;

    // Якщо це пряме посилання (https:// або http://), використовуємо його напряму
    if (profileImg.startsWith("https://") || profileImg.startsWith("http://")) {
      setAvatarUrls(prev => ({ ...prev, [userId]: profileImg }));
      return;
    }

    // Якщо це /api/files/ URL, отримуємо fileId
    if (!profileImg.startsWith("/api/files/")) {
      return;
    }

    const fileId = profileImg.split("/api/files/")[1];
    if (!fileId) return;

    // Перевіряємо, чи вже завантажуємо або завантажили
    if (avatarUrls[userId] || loadingAvatars[userId]) {
      return;
    }

    setLoadingAvatars(prev => ({ ...prev, [userId]: true }));

    try {
      const blobUrl = await getFileByIdAPI(fileId);
      setAvatarUrls(prev => ({ ...prev, [userId]: blobUrl }));
    } catch (err) {
      console.error(`Error fetching avatar for user ${userId}:`, err);
    } finally {
      setLoadingAvatars(prev => ({ ...prev, [userId]: false }));
    }
  };

  const fetchContentPreview = async (
    contentId: string,
    contentType: string,
    flagId: string
  ) => {
    if (contentPreviews[flagId] || loadingPreviews[flagId]) {
      return;
    }

    setLoadingPreviews(prev => ({ ...prev, [flagId]: true }));

    try {
      const response = await getContentPreviewAPI(contentId, contentType);
      if (response.success) {
        setContentPreviews(prev => ({ ...prev, [flagId]: response.data }));
      } else {
        // Fallback: використовуємо дані з flagged content
        const flaggedItem = flaggedContent.items.find(
          (item: FlaggedContent) => item._id === flagId
        );
        if (flaggedItem) {
          const fallbackContent = createFallbackContent(flaggedItem);
          setContentPreviews(prev => ({ ...prev, [flagId]: fallbackContent }));
        }
      }
    } catch (err) {
      console.error(`Error fetching content preview for ${contentId}:`, err);
      // Fallback: використовуємо дані з flagged content
      const flaggedItem = flaggedContent.items.find(
        (item: FlaggedContent) => item._id === flagId
      );
      if (flaggedItem) {
        const fallbackContent = createFallbackContent(flaggedItem);
        setContentPreviews(prev => ({ ...prev, [flagId]: fallbackContent }));
      } else {
        // showToast("Failed to load content preview", "error");
      }
    } finally {
      setLoadingPreviews(prev => ({ ...prev, [flagId]: false }));
    }
  };

  // Створює fallback контент з даних flagged content
  const createFallbackContent = (flaggedItem: FlaggedContent) => {
    switch (flaggedItem.contentType) {
      case "post":
      case "video":
        return {
          description: flaggedItem.flaggedText,
          _id: flaggedItem.contentId,
        };
      case "message":
      case "dm":
        return {
          content: flaggedItem.flaggedText,
          text: flaggedItem.flaggedText,
          message: flaggedItem.flaggedText,
          _id: flaggedItem.contentId,
        };
      case "comment":
        return {
          comment: flaggedItem.flaggedText,
          text: flaggedItem.flaggedText,
          content: flaggedItem.flaggedText,
          _id: flaggedItem.contentId,
        };
      case "profile_description":
        return {
          bio: flaggedItem.flaggedText,
          description: flaggedItem.flaggedText,
          profileDescription: flaggedItem.flaggedText,
          username: flaggedItem.user?.username,
          firstName: flaggedItem.user?.firstName,
          lastName: flaggedItem.user?.lastName,
          email: flaggedItem.user?.email,
          profileImg: flaggedItem.user?.profileImg,
          userId: flaggedItem.user?._id,
          _id: flaggedItem.contentId,
        };
      default:
        return {
          content: flaggedItem.flaggedText,
          _id: flaggedItem.contentId,
        };
    }
  };

  const fetchReviewContentPreview = async (
    contentId: string,
    contentType: string,
    contentItem: FlaggedContent
  ) => {
    setLoadingReviewPreview(true);

    try {
      const response = await getContentPreviewAPI(contentId, contentType);
      if (response.success) {
        setReviewContentPreview(response.data);
      } else {
        const fallbackContent = createFallbackContent(contentItem);
        setReviewContentPreview(fallbackContent);
      }
    } catch (err) {
      console.error(`Error fetching content preview for review:`, err);
      // Fallback: використовуємо дані з review dialog content
      const fallbackContent = createFallbackContent(contentItem);
      setReviewContentPreview(fallbackContent);
    } finally {
      setLoadingReviewPreview(false);
    }
  };

  const togglePreview = (
    flagId: string,
    contentId: string,
    contentType: string
  ) => {
    const isExpanded = expandedPreviews[flagId];
    setExpandedPreviews(prev => ({ ...prev, [flagId]: !isExpanded }));

    if (!isExpanded && !contentPreviews[flagId]) {
      fetchContentPreview(contentId, contentType, flagId);
    }
  };

  const onClickLinkAppeal = (appeal: Appeal, type: "post" | "profile" | "comment") => {
    setActiveTab(type === "post" ? 1 : type === "profile" ? 2 : 3);
    setReportFilters({
      ...reportFilters,
      status: "",
      [type + "Id"]: appeal.targetId ? appeal.targetId : "",
    });
  }

  const handleReview = async () => {
    if (!reviewDialog.content || !reviewDialog.decision) return;

    try {
      const response = await reviewFlaggedContentAPI(
        reviewDialog.content._id,
        reviewDialog.decision,
        reviewDialog.reason
      );

      if (response.success) {
        showToast(`Content ${reviewDialog.decision} successfully`, "success");
        setReviewDialog({
          open: false,
          content: null,
          decision: "",
          reason: "",
        });
        void invalidateModeration();
      }
    } catch (error) {
      console.error("Error reviewing content:", error);
      showToast("Error reviewing content", "error");
    }
  };

  const handleBanUser = async () => {
    if (!banDialog.user || !banDialog.reason) return;

    try {
      const response = await banUserAPI(
        banDialog.user._id,
        banDialog.reason,
        (banDialog.duration as any) || null
      );

      if (response.success) {
        showToast("User banned successfully", "success");
        setBanDialog({ open: false, user: null, reason: "", duration: "" });
        void invalidateModeration();
      }
    } catch (error) {
      console.error("Error banning user:", error);
      showToast("Error banning user", "error");
    }
  };

  const handleSendUserWarning = async () => {
    try {
      const response = await warningUserAPI(
        warningDialog.user._id,
        warningDialog.message
      );

      if (response.success) {
        showToast("User warned successfully", "success");
        setWarningDialog({ open: false, user: null, message: "" });
        void invalidateModeration();
      }
    } catch (error) {
      console.error("Error warning user:", error);
      showToast("Error warning user", "error");
    }
  }

  const handleUpdateReportStatus = async () => {
    if (!reportStatusDialog.report || !reportStatusDialog.status) return;

    try {
      let response;
      if (reportStatusDialog.reportType === "post") {
        response = await updatePostReportStatusAPI(
          reportStatusDialog.report._id,
          reportStatusDialog.status,
          reportStatusDialog.moderatorComment || ""
        );
      } else if (reportStatusDialog.reportType === "profile") {
        response = await updateProfileReportStatusAPI(
          reportStatusDialog.report._id,
          reportStatusDialog.status,
          reportStatusDialog.moderatorComment || "",
          reportStatusDialog.duration || ""
        );
        if (reportStatusDialog.status === "userDeleted") {
          // automatic ban if profile report is resolved with userDeleted status
          await banUserAPI(
            (reportStatusDialog.report as ProfileReport).profile?._id,
            reportStatusDialog.report?.reason,
            reportStatusDialog.duration || ""
          );
        }

        if (reportStatusDialog.status !== "userDeleted") {
          await unbanUserAPI(
            (reportStatusDialog.report as ProfileReport).profile?._id,
            reportStatusDialog.moderatorComment || "Unbanned after profile report resolution",
          );
        }

      } else if (reportStatusDialog.reportType === "comment") {
        response = await updateCommentReportStatusAPI(
          reportStatusDialog.report._id,
          reportStatusDialog.status,
          reportStatusDialog.moderatorComment || ""
        );
      }

      if (response?.success) {
        showToast("Report status updated successfully", "success");
        void invalidateModeration();
        setReportStatusDialog({ open: false, report: null, status: "", moderatorComment: "", reportType: null });
      }
    } catch (error: any) {
      console.error("Error updating report status:", error);
      showToast(error.message || "Error updating report status", "error");
    }
  };

  const handleUpdateAppealStatus = async () => {
    if (!appealStatusDialog.appeal || !appealStatusDialog.status) return;

    try {
      const response = await updateAppealStatusAPI(
        appealStatusDialog.appeal._id,
        appealStatusDialog.status,
        appealStatusDialog.reviewNotes
      );

      if (response.success) {
        showToast("Appeal status updated successfully", "success");
        setAppealStatusDialog({
          open: false,
          appeal: null,
          status: "",
          reviewNotes: "",
        });
        void invalidateModeration();
      }
    } catch (error) {
      console.error("Error updating appeal status:", error);
      showToast("Error updating appeal status", "error");
    }
  };

  const handleQuickUpdateAppealStatus = async (
    appeal: Appeal,
    status: "approved" | "rejected"
  ) => {
    try {
      const response = await updateAppealStatusAPI(appeal._id, status, "");

      if (response.success) {
        showToast(
          `Appeal ${status === "approved" ? "approved" : "rejected"
          } successfully`,
          "success"
        );
        void invalidateModeration();
      }
    } catch (error) {
      console.error("Error updating appeal status:", error);
      showToast("Error updating appeal status", "error");
    }
  };

  const handleUpdatePostStatus = async () => {
    if (!postStatusDialog.postId || !postStatusDialog.status) return;

    try {
      const response = await updatePostStatusAPI(
        postStatusDialog.postId,
        postStatusDialog.status,
        postStatusDialog.reason
      );

      if (response.success) {
        showToast("Post status updated successfully", "success");
        setPostStatusDialog({
          open: false,
          postId: null,
          status: "",
          reason: "",
        });
        void invalidateModeration();
      }
    } catch (error) {
      console.error("Error updating post status:", error);
      showToast("Error updating post status", "error");
    }
  };

  const handleBulkAction = async (decision: string) => {
    if (selectedItems.length === 0) return;

    try {
      const response = await bulkReviewFlaggedContentAPI(
        selectedItems,
        decision,
        `Bulk ${decision} action`
      );

      if (response.success) {
        showToast(`Bulk ${decision} completed successfully`, "success");
        setSelectedItems([]);
        void invalidateModeration();
      }
    } catch (error) {
      console.error("Error performing bulk action:", error);
      showToast("Error performing bulk action", "error");
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case "high":
        return classes.severityHigh;
      case "medium":
        return classes.severityMedium;
      case "low":
        return classes.severityLow;
      default:
        return classes.severityLow;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const fetchMediaUrl = async (mediaUrl: string, key: string) => {
    if (!mediaUrl) return;

    // Якщо це пряме посилання (https:// або http://), використовуємо його напряму
    if (mediaUrl.startsWith("https://") || mediaUrl.startsWith("http://")) {
      setMediaUrls(prev => ({ ...prev, [key]: mediaUrl }));
      return;
    }

    // Якщо це /api/files/ URL, отримуємо fileId
    if (!mediaUrl.startsWith("/api/files/")) {
      return;
    }

    const fileId = mediaUrl.split("/api/files/")[1];
    if (!fileId) return;

    // Перевіряємо, чи вже завантажуємо або завантажили
    if (mediaUrls[key] || loadingMedia[key]) {
      return;
    }

    setLoadingMedia(prev => ({ ...prev, [key]: true }));

    try {
      const blobUrl = await getFileByIdAPI(fileId);
      setMediaUrls(prev => ({ ...prev, [key]: blobUrl }));
    } catch (err) {
      console.error(`Error fetching media for ${key}:`, err);
    } finally {
      setLoadingMedia(prev => ({ ...prev, [key]: false }));
    }
  };

  const getFilterLabel = (activeTab: number): string => {
    switch (activeTab) {
      case 1:
        return "Post ID";
      case 2:
        return "Profile ID";
      case 3:
        return "Comment ID";
      case 4:
        return "Appeal ID";
      default:
        return "Profile or User ID";
    }
  };

  const renderContentPreview = (content: any, contentType: string) => {
    if (!content) return null;

    switch (contentType) {
      case "post":
        return (
          <Box>
            {content.description && (
              <Typography
                variant="body2"
                className={classes.contentPreviewText}
                style={{ color: "#fff", marginBottom: "8px" }}
              >
                {content.description}
              </Typography>
            )}
            {content.media && content.media.length > 0 && (
              <Box>
                {content.media.map((media: any, index: number) => {
                  const mediaUrl = media.url || media.mediaUrl;
                  const mediaKey = `${content._id || "media"}_${index}`;

                  // Завантажуємо медіа через API якщо потрібно
                  if (mediaUrl && mediaUrl.startsWith("/api/files/")) {
                    if (!mediaUrls[mediaKey] && !loadingMedia[mediaKey]) {
                      fetchMediaUrl(mediaUrl, mediaKey);
                    }
                  }

                  const displayUrl = mediaUrls[mediaKey] || mediaUrl;

                  if (media.type === "image" || media.type === "photo") {
                    return (
                      <Box key={index} style={{ marginBottom: "8px" }}>
                        {loadingMedia[mediaKey] ? (
                          <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            style={{ minHeight: "200px" }}
                          >
                            <CircularProgress size={24} />
                          </Box>
                        ) : (
                          <img
                            src={displayUrl}
                            alt={`Content ${index + 1}`}
                            className={classes.contentPreviewImage}
                            onError={e => {
                              (e.target as HTMLImageElement).src =
                                defaultAvatars;
                            }}
                          />
                        )}
                      </Box>
                    );
                  } else if (media.type === "video") {
                    return (
                      <Box key={index} style={{ marginBottom: "8px" }}>
                        {loadingMedia[mediaKey] ? (
                          <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            style={{ minHeight: "200px" }}
                          >
                            <CircularProgress size={24} />
                          </Box>
                        ) : (
                          <video
                            src={displayUrl}
                            controls
                            className={classes.contentPreviewVideo}
                          >
                            Your browser does not support the video tag.
                          </video>
                        )}
                      </Box>
                    );
                  }
                  return null;
                })}
              </Box>
            )}
          </Box>
        );
      case "message":
      case "dm":
        return (
          <Box>
            <Typography
              variant="body2"
              className={classes.contentPreviewText}
              style={{ color: "#fff" }}
            >
              {content.content ||
                content.text ||
                content.message ||
                "No content available"}
            </Typography>
            {content.mediaUrl && (
              <Box style={{ marginTop: "8px" }}>
                {content.mediaUrl.startsWith("/api/files/") ? (
                  (() => {
                    const mediaKey = `message_${content._id || content.contentId
                      }`;
                    if (!mediaUrls[mediaKey] && !loadingMedia[mediaKey]) {
                      fetchMediaUrl(content.mediaUrl, mediaKey);
                    }
                    return loadingMedia[mediaKey] ? (
                      <CircularProgress size={24} />
                    ) : (
                      <img
                        src={mediaUrls[mediaKey] || content.mediaUrl}
                        alt="Message media"
                        className={classes.contentPreviewImage}
                        onError={e => {
                          (e.target as HTMLImageElement).src = defaultAvatars;
                        }}
                      />
                    );
                  })()
                ) : (
                  <img
                    src={content.mediaUrl}
                    alt="Message media"
                    className={classes.contentPreviewImage}
                    onError={e => {
                      (e.target as HTMLImageElement).src = defaultAvatars;
                    }}
                  />
                )}
              </Box>
            )}
          </Box>
        );
      case "comment":
        return (
          <Typography
            variant="body2"
            className={classes.contentPreviewText}
            style={{ color: "#fff" }}
          >
            {content.commentText ||
              content.text ||
              content.content ||
              "No content available"}
          </Typography>
        );
      case "video":
        return (
          <Box>
            {content.description && (
              <Typography
                variant="body2"
                className={classes.contentPreviewText}
                style={{ color: "#fff", marginBottom: "8px" }}
              >
                {content.description}
              </Typography>
            )}
            {content.videoUrl && (
              <Box>
                {content.videoUrl.startsWith("/api/files/") ? (
                  (() => {
                    const mediaKey = `video_${content._id || content.contentId
                      }`;
                    if (!mediaUrls[mediaKey] && !loadingMedia[mediaKey]) {
                      fetchMediaUrl(content.videoUrl, mediaKey);
                    }
                    return loadingMedia[mediaKey] ? (
                      <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        style={{ minHeight: "200px" }}
                      >
                        <CircularProgress size={24} />
                      </Box>
                    ) : (
                      <video
                        src={mediaUrls[mediaKey] || content.videoUrl}
                        controls
                        className={classes.contentPreviewVideo}
                      >
                        Your browser does not support the video tag.
                      </video>
                    );
                  })()
                ) : (
                  <video
                    src={content.videoUrl}
                    controls
                    className={classes.contentPreviewVideo}
                  >
                    Your browser does not support the video tag.
                  </video>
                )}
              </Box>
            )}
          </Box>
        );
      case "profile_description":
        return (
          <Box>
            <Box
              display="flex"
              alignItems="center"
              style={{ marginBottom: "12px", gap: "12px" }}
            >
              {content.profileImg && (
                <Avatar
                  src={
                    content.profileImg.startsWith("/api/files/")
                      ? (() => {
                        const avatarKey = `profile_avatar_${content.userId || content._id
                          }`;
                        if (
                          !avatarUrls[avatarKey] &&
                          !loadingAvatars[avatarKey]
                        ) {
                          fetchAvatarUrl(
                            content.userId || content._id,
                            content.profileImg
                          );
                        }
                        return avatarUrls[avatarKey] || content.profileImg;
                      })()
                      : content.profileImg
                  }
                  alt={content.username || "User"}
                  style={{ width: 56, height: 56 }}
                >
                  {!content.profileImg &&
                    (content.firstName?.[0] || content.username?.[0] || "U")}
                </Avatar>
              )}
              <Box>
                <Typography
                  variant="subtitle1"
                  style={{ color: "#FE5A97", fontWeight: "bold" }}
                >
                  {content.username ? (
                    <Link
                      to={`/${content.username}`}
                      style={{
                        color: "#FE5A97",
                        textDecoration: "none",
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.textDecoration = "underline";
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.textDecoration = "none";
                      }}
                    >
                      @{content.username}
                    </Link>
                  ) : content.firstName && content.lastName ? (
                    `${content.firstName} ${content.lastName}`
                  ) : (
                    "Unknown User"
                  )}
                </Typography>
                {content.email && (
                  <Typography variant="body2" style={{ color: "#999" }}>
                    {content.email}
                  </Typography>
                )}
              </Box>
            </Box>
            <Typography
              variant="body2"
              className={classes.contentPreviewText}
              style={{ color: "#fff" }}
            >
              <strong>Profile Description:</strong>
            </Typography>
            <Typography
              variant="body2"
              className={classes.contentPreviewText}
              style={{ color: "#fff", marginTop: "8px" }}
            >
              {content.bio ||
                content.description ||
                content.profileDescription ||
                "No profile description available"}
            </Typography>
            {content.website && (
              <Typography
                variant="body2"
                style={{ color: "#FE5A97", marginTop: "8px" }}
              >
                <strong>Website:</strong>{" "}
                <a
                  href={
                    content.website.startsWith("http")
                      ? content.website
                      : `https://${content.website}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "#FE5A97" }}
                >
                  {content.website}
                </a>
              </Typography>
            )}
            {content.location && (
              <Typography
                variant="body2"
                style={{ color: "#ccc", marginTop: "4px" }}
              >
                <strong>Location:</strong> {content.location}
              </Typography>
            )}
          </Box>
        );
      default:
        return (
          <Typography
            variant="body2"
            className={classes.contentPreviewText}
            style={{ color: "#fff" }}
          >
            {JSON.stringify(content, null, 2)}
          </Typography>
        );
    }
  };

  if (loading) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className={classes.container}>
      <Box className={classes.header}>
        <Typography variant="h4" style={{ color: "#FE5A97" }}>
          Content Moderation
        </Typography>
        <Button
          variant="contained"
          startIcon={<Refresh />}
          onClick={() => {
            void invalidateModeration();
          }}
          style={{ backgroundColor: "#FE5A97", color: "#fff" }}
        >
          Refresh
        </Button>
      </Box>

      {/* Tabs */}
      <Box className={classes.tabsContainer}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
          classes={{ indicator: classes.tabIndicator }}
        >
          <Tab label="Flagged Content" className={classes.tab} />
          <Tab label="Post Reports" className={classes.tab} />
          <Tab label="Profile Reports" className={classes.tab} />
          <Tab label="Comment Reports" className={classes.tab} />
          <Tab label="Appeals" className={classes.tab} />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {activeTab === 0 && (
        <>
          {/* Statistics */}
          {stats && (
            <Grid container spacing={2} className={classes.statsGrid}>
              <Grid item xs={12} sm={6} md={3}>
                <Card className={classes.statCard}>
                  <CardContent>
                    <Typography variant="h6">Total Flags</Typography>
                    <Typography className={classes.statValue}>
                      {stats.totalFlags}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card className={classes.statCard}>
                  <CardContent>
                    <Typography variant="h6">Pending Review</Typography>
                    <Typography className={classes.statValue}>
                      {stats.pendingFlags}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card className={classes.statCard}>
                  <CardContent>
                    <Typography variant="h6">Banned Users</Typography>
                    <Typography className={classes.statValue}>
                      {stats.bannedUsers}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card className={classes.statCard}>
                  <CardContent>
                    <Typography variant="h6">High Risk Users</Typography>
                    <Typography className={classes.statValue}>
                      {stats.highRiskUsers}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Filters */}
          <Card className={classes.filtersCard}>
            <ReportCustomFilters<FlaggedFilters>
              filters={filters}
              setFilters={setFilters}
              fields={[
                {
                  key: "status",
                  label: "Status",
                  type: "select",
                  options: [
                    { value: "all", label: "All" },
                    { value: "pending", label: "Pending" },
                    { value: "approved", label: "Approved" },
                    { value: "rejected", label: "Rejected" },
                    { value: "escalated", label: "Escalated" },
                  ],
                },
                {
                  key: "severity",
                  label: "Severity",
                  type: "select",
                  options: [
                    { value: "", label: "All" },
                    { value: "high", label: "High" },
                    { value: "medium", label: "Medium" },
                    { value: "low", label: "Low" },
                  ],
                },
                {
                  key: "contentType",
                  label: "Content Type",
                  type: "select",
                  options: [
                    { value: "", label: "All" },
                    { value: "post", label: "Post" },
                    { value: "message", label: "Message" },
                    { value: "comment", label: "Comment" },
                    { value: "video", label: "Video" },
                    { value: "profile_description", label: "Profile Description" },
                  ],
                },
                {
                  key: "sortOrder",
                  label: "Sort by date",
                  type: "select",
                  options: [
                    { value: "newest", label: "Newest first" },
                    { value: "oldest", label: "Oldest first" },
                  ],
                },
                {
                  key: "dateRange",
                  label: "Date range",
                  type: "select",
                  options: [
                    { value: "all", label: "All time" },
                    { value: "24h", label: "Last 24 hours" },
                    { value: "7d", label: "Last 7 days" },
                    { value: "30d", label: "Last 30 days" },
                  ],
                },
              ]}
            />

          </Card>

          {/* Bulk Actions */}
          {selectedItems.length > 0 && (
            <Box className={classes.bulkActions}>
              <Typography variant="h6" style={{ color: "#fff" }}>
                {selectedItems.length} items selected
              </Typography>
              <Button
                variant="contained"
                className={classes.approveButton}
                onClick={() => handleBulkAction("approve")}
              >
                Approve All
              </Button>
              <Button
                variant="contained"
                className={classes.rejectButton}
                onClick={() => handleBulkAction("reject")}
              >
                Reject All
              </Button>
              <Button
                variant="contained"
                className={classes.escalateButton}
                onClick={() => handleBulkAction("escalate")}
              >
                Escalate All
              </Button>
            </Box>
          )}

          {/* Flagged Content List */}
          {flaggedContent && visibleFlaggedContent.length === 0 ? (
            <Card className={classes.contentCard}>
              <CardContent>
                <Typography
                  variant="h6"
                  style={{ color: "#fff", textAlign: "center" }}
                >
                  No flagged content found with current filters.
                </Typography>
              </CardContent>
            </Card>
          ) : (
            visibleFlaggedContent.map((item: FlaggedContent) => (
              <Card key={item._id} className={classes.contentCard}>
                <CardContent>
                  <Box className={classes.contentHeader}>
                    <Box className={classes.userInfo}>
                      {loadingAvatars[item.user?._id || ""] ? (
                        <Avatar>
                          <CircularProgress size={24} />
                        </Avatar>
                      ) : (
                        <Avatar
                          src={
                            item.user?._id && avatarUrls[item.user._id]
                              ? avatarUrls[item.user._id]
                              : item.user?.profileImg
                                ? item.user.profileImg
                                : defaultAvatars
                          }
                          alt={item.user?.username || "User"}
                        >
                          {!item.user?.profileImg &&
                            !avatarUrls[item.user?._id || ""] &&
                            (item.user?.firstName?.[0] ||
                              item.user?.username?.[0] ||
                              "U")}
                        </Avatar>
                      )}
                      <Box>
                        <Typography variant="h6" style={{ color: "#fff" }}>
                          {item.user?.username ? (
                            <Link
                              to={`/${item.user.username}`}
                              style={{
                                color: "#FE5A97",
                                textDecoration: "none",
                              }}
                              onMouseEnter={e => {
                                e.currentTarget.style.textDecoration =
                                  "underline";
                              }}
                              onMouseLeave={e => {
                                e.currentTarget.style.textDecoration = "none";
                              }}
                            >
                              {item.user.username}
                            </Link>
                          ) : (
                            item.user?.firstName || "Unknown User"
                          )}
                        </Typography>
                        <Typography>{item.user?.warningsCount || 0} warnings</Typography>
                        <Typography variant="body2" style={{ color: "#ccc" }}>
                          {item.user?.email || "No email available"}
                        </Typography>
                      </Box>
                    </Box>
                    <Box
                      display="flex"
                      alignItems="center"
                      style={{ gap: "8px" }}
                    >
                      <Chip
                        label={item.severity.toUpperCase()}
                        className={getSeverityColor(item.severity)}
                        size="small"
                      />
                      <Chip
                        label={
                          item.contentType === "profile_description"
                            ? "Profile Description"
                            : item.contentType.toUpperCase()
                        }
                        variant="outlined"
                        size="small"
                        style={{ color: "#fff", borderColor: "#FE5A97" }}
                      />
                      <Chip
                        label={item.status.toUpperCase()}
                        variant="outlined"
                        size="small"
                        style={{ color: "#fff", borderColor: "#333" }}
                      />
                    </Box>
                  </Box>

                  <Divider
                    style={{ backgroundColor: "#333", margin: "16px 0" }}
                  />

                  <Box
                    display="flex"
                    alignItems="flex-start"
                    style={{ gap: "16px" }}
                  >
                    <Box flex={1}>
                      <Typography
                        variant="body1"
                        style={{ marginBottom: "8px", color: "#fff" }}
                      >
                        <strong>Reason:</strong> {item.reason}
                      </Typography>
                      <Typography
                        variant="body2"
                        style={{ marginBottom: "8px", color: "#fff" }}
                      >
                        <strong>Flagged Text:</strong> {item.flaggedText}
                      </Typography>
                      {item.matchedTerms && item.matchedTerms.length > 0 && (
                        <Box style={{ marginBottom: "8px" }}>
                          <Typography
                            variant="body2"
                            style={{ marginBottom: "4px", color: "#fff" }}
                          >
                            <strong>Matched Terms:</strong>
                          </Typography>
                          <Box
                            display="flex"
                            flexWrap="wrap"
                            style={{ gap: "4px" }}
                          >
                            {item.matchedTerms.map(
                              (
                                term: FlaggedContent["matchedTerms"][number],
                                index: number
                              ) => (
                                <Chip
                                  key={index}
                                  label={`${term.term} (${term.type})`}
                                  size="small"
                                  style={{
                                    backgroundColor:
                                      term.type === "direct"
                                        ? "#f44336"
                                        : "#ff9800",
                                    color: "#fff",
                                    fontSize: "10px",
                                  }}
                                />
                              ))}
                          </Box>
                        </Box>
                      )}
                      <Typography variant="body2" style={{ color: "#ccc" }}>
                        <strong>Flagged:</strong> {formatDate(item.createdAt)}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Content Preview Section */}
                  <Box style={{ marginTop: "16px" }}>
                    <Button
                      className={classes.expandButton}
                      onClick={() =>
                        togglePreview(
                          item._id,
                          item.contentId,
                          item.contentType
                        )
                      }
                      size="small"
                    >
                      {expandedPreviews[item._id] ? "Hide" : "Show"} Content
                      Preview
                    </Button>
                    {expandedPreviews[item._id] && (
                      <Box className={classes.contentPreviewContainer}>
                        {loadingPreviews[item._id] ? (
                          <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            style={{ minHeight: "100px" }}
                          >
                            <CircularProgress />
                          </Box>
                        ) : contentPreviews[item._id] ? (
                          <Box>
                            <Typography
                              variant="subtitle2"
                              style={{ color: "#FE5A97", marginBottom: "8px" }}
                            >
                              Content Preview (
                              {item.contentType === "profile_description"
                                ? "Profile Description"
                                : item.contentType}
                              )
                            </Typography>
                            {renderContentPreview(
                              contentPreviews[item._id],
                              item.contentType
                            )}
                          </Box>
                        ) : (
                          <Typography variant="body2" style={{ color: "#999" }}>
                            Content preview not available
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>

                  <Box
                    className={classes.actionButtons}
                    style={{ marginTop: "16px" }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedItems.includes(item._id)}
                          onChange={e => {
                            if (e.target.checked) {
                              setSelectedItems([...selectedItems, item._id]);
                            } else {
                              setSelectedItems(
                                selectedItems.filter(id => id !== item._id)
                              );
                            }
                          }}
                          style={{ color: "#FE5A97" }}
                        />
                      }
                      label="Select"
                      style={{ color: "#fff" }}
                    />
                    <Button
                      variant="contained"
                      startIcon={<Visibility style={{ fontSize: "24px" }} />}
                      onClick={() => {
                        setReviewDialog({
                          open: true,
                          content: item,
                          decision: "",
                          reason: "",
                        });
                        // Завантажуємо прев'ю контенту для review dialog
                        if (item.contentId && item.contentType) {
                          fetchReviewContentPreview(
                            item.contentId,
                            item.contentType,
                            item
                          );
                        }
                      }}
                      style={{ backgroundColor: "#2196f3", color: "#fff" }}
                    >
                      Review
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<Person />}
                      onClick={() =>
                        setBanDialog({
                          open: true,
                          user: item.user || {
                            _id: "unknown",
                            username: "Unknown User",
                          },
                          reason: "",
                          duration: "",
                        })
                      }
                      className={classes.banButton}
                    >
                      Ban User
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<Person />}
                      onClick={() =>
                        setWarningDialog({
                          open: true,
                          user: item.user || {
                            _id: "unknown",
                            username: "Unknown User",
                          },
                          message: "",
                        })
                      }
                      className={classes.escalateButton}
                    >
                      Warning
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            ))
          )}
        </>
      )}

      {/* Post Reports Tab */}
      {activeTab === 1 && (
        <>
          {/* Filters for Post Reports */}
          <Card className={classes.filtersCard}>
            <ReportFilters
              filters={reportFilters}
              setFilters={setReportFilters}
              sortOrder={sortOrder}
              setSortOrder={setSortOrder}
              dateRange={dateRange}
              setDateRange={setDateRange}
              activeTab={activeTab}
            />
          </Card>

          {/* Post Reports List */}
          {postReports && visiblePostReports.length === 0 ? (
            <Card className={classes.contentCard}>
              <CardContent>
                <Typography
                  variant="h6"
                  style={{ color: "#fff", textAlign: "center" }}
                >
                  No post reports found with current filters.
                </Typography>
              </CardContent>
            </Card>
          ) : (
            visiblePostReports.map((report: PostReport) => (
              <ReportPostCard key={report._id} report={report} reportType="post" loadingAvatars={loadingAvatars} avatarUrls={avatarUrls} mediaUrls={mediaUrls} loadingMedia={loadingMedia} fetchMediaUrl={fetchMediaUrl} setReportStatusDialog={setReportStatusDialog} />
            ))
          )}
        </>
      )}
      {/* Profile Reports Tab */}
      {activeTab === 2 && (
        <>
          {/* Filters for Post Reports */}
          <Card className={classes.filtersCard}>
            <ReportFilters
              filters={reportFilters}
              setFilters={setReportFilters}
              sortOrder={sortOrder}
              setSortOrder={setSortOrder}
              dateRange={dateRange}
              setDateRange={setDateRange}
              activeTab={activeTab}
            />
          </Card>

          {/* Profile Reports List */}
          {profileReports && visibleProfileReports.length === 0 ? (
            <Card className={classes.contentCard}>
              <CardContent>
                <Typography
                  variant="h6"
                  style={{ color: "#fff", textAlign: "center" }}
                >
                  No profile reports found with current filters.
                </Typography>
              </CardContent>
            </Card>
          ) :
            (
              visibleProfileReports.map((report: ProfileReport) => (
                <ReportProfileCard key={report._id} report={report} reportType="profile" loadingAvatars={loadingAvatars} avatarUrls={avatarUrls} mediaUrls={mediaUrls} loadingMedia={loadingMedia} fetchMediaUrl={fetchMediaUrl} setReportStatusDialog={setReportStatusDialog} loadProfileReports={invalidateModeration} />
              ))
            )}
        </>
      )}

      {activeTab === 3 && (
        <>
          {/* Filters for Comments Reports */}
          <Card className={classes.filtersCard}>
            <ReportFilters
              filters={reportFilters}
              setFilters={setReportFilters}
              sortOrder={sortOrder}
              setSortOrder={setSortOrder}
              dateRange={dateRange}
              setDateRange={setDateRange}
              activeTab={activeTab}
            />
          </Card>

          {/* Comment Reports List */}
          {commentReports && visibleCommentReports.length === 0 ? (
            <Card className={classes.contentCard}>
              <CardContent>
                <Typography
                  variant="h6"
                  style={{ color: "#fff", textAlign: "center" }}
                >
                  No comment reports found with current filters.
                </Typography>
              </CardContent>
            </Card>
          ) : (
            visibleCommentReports.map((report: CommentReport) => (
              <ReportCommentCard key={report._id} report={report} reportType="comment" loadingAvatars={loadingAvatars} avatarUrls={avatarUrls} mediaUrls={mediaUrls} loadingMedia={loadingMedia} fetchMediaUrl={fetchMediaUrl} setReportStatusDialog={setReportStatusDialog} />
            ))
          )}
        </>
      )}

      {/* Appeals Tab */}
      {activeTab === 4 && (
        <>
          {/* Filters for Appeals */}
          <Card className={classes.filtersCard}>
            <ReportCustomFilters<AppealFilters>
              filters={appealFilters}
              setFilters={setAppealFilters}
              fields={[
                {
                  key: "status",
                  label: "Status",
                  type: "select",
                  options: [
                    { value: "", label: "All" },
                    { value: "pending", label: "Pending" },
                    { value: "reviewing", label: "Reviewing" },
                    { value: "approved", label: "Approved" },
                    { value: "rejected", label: "Rejected" },
                  ],
                },
                { key: "targetId", label: "Appeal ID", type: "text" },
                { key: "userId", label: "User ID", type: "text" },
                {
                  key: "sortOrder",
                  label: "Sort by date",
                  type: "select",
                  options: [
                    { value: "newest", label: "Newest first" },
                    { value: "oldest", label: "Oldest first" },
                  ],
                },
                {
                  key: "dateRange",
                  label: "Date range",
                  type: "select",
                  options: [
                    { value: "all", label: "All time" },
                    { value: "24h", label: "Last 24 hours" },
                    { value: "7d", label: "Last 7 days" },
                    { value: "30d", label: "Last 30 days" },
                  ],
                },
              ]}
            />

          </Card>

          {/* Appeals List */}
          {appeals && visibleAppeals.length === 0 ? (
            <Card className={classes.contentCard}>
              <CardContent>
                <Typography
                  variant="h6"
                  style={{ color: "#fff", textAlign: "center" }}
                >
                  No appeals found with current filters.
                </Typography>
              </CardContent>
            </Card>
          ) : (
            visibleAppeals.map((appeal: Appeal) => (
              <AppealCard key={appeal._id} appeal={appeal} loadingAvatars={loadingAvatars} avatarUrls={avatarUrls} loadAppeals={invalidateModeration} onClickLinkAppeal={onClickLinkAppeal} setAppealStatusDialog={setAppealStatusDialog} />
            ))
          )}
        </>
      )}

      {/* Review Dialog */}
      <Dialog
        open={reviewDialog.open}
        onClose={() => {
          setReviewDialog({
            open: false,
            content: null,
            decision: "",
            reason: "",
          });
          setReviewContentPreview(null);
        }}
        className={classes.reviewDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Review Flagged Content</DialogTitle>
        <DialogContent>
          {/* Content Preview in Review Dialog */}
          {reviewDialog.content && (
            <Box
              className={classes.contentPreviewContainer}
              style={{ marginBottom: "16px" }}
            >
              <Typography
                variant="subtitle1"
                style={{ color: "#FE5A97", marginBottom: "8px" }}
              >
                Content Preview (
                {reviewDialog.content.contentType === "profile_description"
                  ? "Profile Description"
                  : reviewDialog.content.contentType}
                )
              </Typography>
              {loadingReviewPreview ? (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  style={{ minHeight: "100px" }}
                >
                  <CircularProgress />
                </Box>
              ) : reviewContentPreview ? (
                renderContentPreview(
                  reviewContentPreview,
                  reviewDialog.content.contentType,
                )
              ) : (
                <Typography variant="body2" style={{ color: "#999" }}>
                  Content preview not available
                </Typography>
              )}
              <Divider style={{ backgroundColor: "#333", margin: "16px 0" }} />
              <Typography
                variant="body2"
                style={{ color: "#fff", marginBottom: "8px" }}
              >
                <strong>Flagged Text:</strong>{" "}
                {reviewDialog.content.flaggedText}
              </Typography>
              <Typography variant="body2" style={{ color: "#fff" }}>
                <strong>Reason:</strong> {reviewDialog.content.reason}
              </Typography>
            </Box>
          )}
          <FormControl fullWidth margin="normal" variant="outlined">
            <InputLabel style={{ color: "#fff" }}
            >Decision</InputLabel>
            <Select
              value={reviewDialog.decision}
              onChange={e =>
                setReviewDialog({
                  ...reviewDialog,
                  decision: e.target.value as string,
                })
              }
              label="Decision"
              style={{
                backgroundColor: "#2a2a2a",
                color: "#fff",
                borderRadius: 4,
              }}
              inputProps={{
                style: { color: "#fff" },
              }}
            >
              <MenuItem value="approve" style={{ color: "#fff" }}>
                Approve
              </MenuItem>
              <MenuItem value="reject" style={{ color: "#fff" }}>
                Reject
              </MenuItem>
              <MenuItem value="escalate" style={{ color: "#fff" }}>
                Escalate
              </MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            multiline
            variant="outlined"
            rows={3}
            label="Reason (optional)"
            value={reviewDialog.reason}
            onChange={e =>
              setReviewDialog({ ...reviewDialog, reason: e.target.value })
            }
            margin="normal"
            style={{ backgroundColor: "#2a2a2a", color: "#fff" }}
            InputProps={{
              style: { color: "#fff" },
            }}
            InputLabelProps={{
              style: { color: "#fff" },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button style={{ color: "#fff" }}
            onClick={() =>
              setReviewDialog({
                open: false,
                content: null,
                decision: "",
                reason: "",
              })
            }
          >
            Cancel
          </Button>
          <Button
            onClick={handleReview}
            variant="contained"
            style={{ backgroundColor: "#FE5A97", color: "#fff" }}
          >
            Submit Review
          </Button>
        </DialogActions>
      </Dialog>

      {/* Ban User Dialog */}
      <Dialog
        open={banDialog.open}
        onClose={() =>
          setBanDialog({ open: false, user: null, reason: "", duration: "" })
        }
        className={classes.reviewDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Ban User</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Ban Reason"
            value={banDialog.reason}
            onChange={e =>
              setBanDialog({ ...banDialog, reason: e.target.value })
            }
            margin="normal"
            required
            style={{ color: "#fff" }}
            InputProps={{ style: { color: "#fff" } }}
            InputLabelProps={{ style: { color: "#fff" } }}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel style={{ color: "#fff" }}>
              Duration (optional)
            </InputLabel>
            <Select
              value={banDialog.duration}
              onChange={e =>
                setBanDialog({
                  ...banDialog,
                  duration: e.target.value as string,
                })
              }
              label="Duration (optional)"
              style={{ color: "#fff" }}
            >
              <MenuItem value="" style={{ color: "#fff" }}>
                Permanent
              </MenuItem>
              <MenuItem value="1d" style={{ color: "#fff" }}>
                1 Day
              </MenuItem>
              <MenuItem value="7d" style={{ color: "#fff" }}>
                7 Days
              </MenuItem>
              <MenuItem value="30d" style={{ color: "#fff" }}>
                30 Days
              </MenuItem>
              <MenuItem value="90d" style={{ color: "#fff" }}>
                90 Days
              </MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setBanDialog({
                open: false,
                user: null,
                reason: "",
                duration: "",
              })
            }
          >
            Cancel
          </Button>
          <Button
            onClick={handleBanUser}
            variant="contained"
            className={classes.banButton}
          >
            Ban User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Warning user dialog */}
      <Dialog
        open={warningDialog.open}
        onClose={() =>
          setWarningDialog({ open: false, user: null, message: "" })
        }
        className={classes.reviewDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Warning User</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Warning Message"
            value={warningDialog.message}
            onChange={e =>
              setWarningDialog({ ...warningDialog, message: e.target.value })
            }
            margin="normal"
            style={{ color: "#fff" }}
            InputProps={{ style: { color: "#fff" } }}
            InputLabelProps={{ style: { color: "#fff" } }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setWarningDialog({
                open: false,
                user: null,
                message: "",
              })
            }
          >
            Cancel
          </Button>
          <Button
            onClick={handleSendUserWarning}
            variant="contained"
            className={classes.escalateButton}
          >
            Warning
          </Button>
        </DialogActions>
      </Dialog>

      {/* Report Post Status Dialog */}
      <Dialog
        open={reportStatusDialog.open}
        onClose={() =>
          setReportStatusDialog({ open: false, report: null, status: "", moderatorComment: "", reportType: "profile" })
        }
        className={classes.reviewDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Update Report Status</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel style={{ color: "#fff" }}>Status</InputLabel>
            <Select
              value={reportStatusDialog.status}
              onChange={e =>
                setReportStatusDialog({
                  ...reportStatusDialog,
                  status: e.target.value as string,
                })
              }
              label="Status"
              style={{ color: "#fff" }}
            >
              <MenuItem value="open" style={{ color: "#fff" }}>
                Open
              </MenuItem>
              <MenuItem value="resolved" style={{ color: "#fff" }}>
                Resolved
              </MenuItem>
              <MenuItem value={reportTypeStatus} style={{ color: "#fff" }}>
                {reportStatusDialog.reportType === "comment" ? "Comment Banned" : "Post Deleted / Banned"}
              </MenuItem>
            </Select>
          </FormControl>
          {reportStatusDialog.status === "resolved" && (
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Moderator Comment"
              value={reportStatusDialog.moderatorComment || ""}
              required
              onChange={e =>
                setReportStatusDialog({
                  ...reportStatusDialog,
                  moderatorComment: e.target.value,
                })
              }
              margin="normal"
              style={{ color: "#fff" }}
              InputProps={{ style: { color: "#fff" } }}
              InputLabelProps={{ style: { color: "#fff" } }}
            />
          )}
        </DialogContent>
        <DialogActions className={classes.modalActions}>
          <Button
            onClick={() =>
              setReportStatusDialog({ open: false, report: null, status: "", moderatorComment: "" })
            }
            className={classes.cancelButton}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateReportStatus}
            variant="contained"
            className={classes.sendButton}
          >
            Update Status
          </Button>
        </DialogActions>
      </Dialog>

      {/* Report Profile Status Dialog */}
      <Dialog
        open={reportStatusDialog.open}
        onClose={() =>
          setReportStatusDialog({ open: false, report: null, status: "", moderatorComment: "", reportType: "profile" })
        }
        className={classes.reviewDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Update Report Status</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel style={{ color: "#fff" }}>Status</InputLabel>
            <Select
              value={reportStatusDialog.status}
              onChange={e =>
                setReportStatusDialog({
                  ...reportStatusDialog,
                  status: e.target.value as string,
                })
              }
              label="Status"
              style={{ color: "#fff" }}
            >
              <MenuItem value="open" style={{ color: "#fff" }}>
                Open
              </MenuItem>
              <MenuItem value="resolved" style={{ color: "#fff" }}>
                Resolved
              </MenuItem>
              <MenuItem value={reportTypeStatus} style={{ color: "#fff" }}>
                {reportStatusDialog.reportType === "comment"
                  ? "Comment Banned"
                  : `${reportStatusDialog.reportType && (reportStatusDialog.reportType).charAt(0).toUpperCase() + reportStatusDialog.reportType.slice(1)} Banned`}
              </MenuItem>
            </Select>
          </FormControl>
          {reportStatusDialog.reportType === "profile" && reportStatusDialog.status === "userDeleted" && (
            <FormControl fullWidth margin="normal">
              <InputLabel style={{ color: "#fff" }}>
                Duration (optional)
              </InputLabel>
              <Select
                value={reportStatusDialog.duration}
                onChange={e =>
                  setReportStatusDialog({
                    ...reportStatusDialog,
                    duration: e.target.value as string,
                  })
                }
                label="Duration (optional)"
                style={{ color: "#fff" }}
              >
                <MenuItem value="" style={{ color: "#fff" }}>
                  Permanent
                </MenuItem>
                <MenuItem value="1" style={{ color: "#fff" }}>
                  1 Day
                </MenuItem>
                <MenuItem value="7" style={{ color: "#fff" }}>
                  7 Days
                </MenuItem>
                <MenuItem value="30" style={{ color: "#fff" }}>
                  30 Days
                </MenuItem>
                <MenuItem value="90" style={{ color: "#fff" }}>
                  90 Days
                </MenuItem>
              </Select>
            </FormControl>
          )}
          {reportStatusDialog.status === "resolved" && (
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Moderator Comment"
              value={reportStatusDialog.moderatorComment || ""}
              onChange={e =>
                setReportStatusDialog({
                  ...reportStatusDialog,
                  moderatorComment: e.target.value,
                })
              }
              margin="normal"
              style={{ color: "#fff" }}
              InputProps={{ style: { color: "#fff" } }}
              InputLabelProps={{ style: { color: "#fff" } }}
            />
          )}
        </DialogContent>
        <DialogActions className={classes.modalActions}>
          <Button
            onClick={() =>
              setReportStatusDialog({ open: false, report: null, status: "", moderatorComment: "" })
            }
            className={classes.cancelButton}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateReportStatus}
            variant="contained"
            className={classes.sendButton}
          >
            Update Status
          </Button>
        </DialogActions>
      </Dialog>

      {/* Appeal Status Dialog */}
      <Dialog
        open={appealStatusDialog.open}
        onClose={() =>
          setAppealStatusDialog({
            open: false,
            appeal: null,
            status: "",
            reviewNotes: "",
          })
        }
        className={classes.reviewDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Update Appeal Status</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel style={{ color: "#fff" }}>Status</InputLabel>
            <Select
              value={appealStatusDialog.status}
              onChange={e =>
                setAppealStatusDialog({
                  ...appealStatusDialog,
                  status: e.target.value as string,
                })
              }
              label="Status"
              style={{ color: "#fff" }}
            >
              <MenuItem value="pending" style={{ color: "#fff" }}>Pending</MenuItem>
              <MenuItem value="reviewing" style={{ color: "#fff" }}>Reviewing</MenuItem>
              <MenuItem value="approved" style={{ color: "#fff" }}>Approved</MenuItem>
              <MenuItem value="rejected" style={{ color: "#fff" }}>Rejected</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Review Notes (optional)"
            value={appealStatusDialog.reviewNotes}
            onChange={e =>
              setAppealStatusDialog({
                ...appealStatusDialog,
                reviewNotes: e.target.value,
              })
            }
            margin="normal"
            style={{ color: "#fff" }}
            InputProps={{ style: { color: "#fff" } }}
            InputLabelProps={{ style: { color: "#fff" } }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setAppealStatusDialog({
                open: false,
                appeal: null,
                status: "",
                reviewNotes: "",
              })
            }
            style={{ color: "#fff" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateAppealStatus}
            variant="contained"
            style={{ backgroundColor: "#FE5A97", color: "#fff" }}
          >
            Update Status
          </Button>
        </DialogActions>
      </Dialog>

      {/* Post Status Dialog */}
      <Dialog
        open={postStatusDialog.open}
        onClose={() =>
          setPostStatusDialog({
            open: false,
            postId: null,
            status: "",
            reason: "",
          })
        }
        className={classes.reviewDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Change Post Status</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel style={{ color: "#fff" }}>Status</InputLabel>
            <Select
              value={postStatusDialog.status}
              onChange={e =>
                setPostStatusDialog({
                  ...postStatusDialog,
                  status: e.target.value as string,
                })
              }
              label="Status"
              style={{ color: "#fff" }}
            >
              <MenuItem value="active" style={{ color: "#fff" }}>
                Active
              </MenuItem>
              <MenuItem value="hidden" style={{ color: "#fff" }}>
                Hidden
              </MenuItem>
              <MenuItem value="deleted" style={{ color: "#fff" }}>
                Deleted
              </MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Reason (optional)"
            value={postStatusDialog.reason}
            onChange={e =>
              setPostStatusDialog({
                ...postStatusDialog,
                reason: e.target.value,
              })
            }
            margin="normal"
            style={{ color: "#fff" }}
            InputProps={{ style: { color: "#fff" } }}
            InputLabelProps={{ style: { color: "#fff" } }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setPostStatusDialog({
                open: false,
                postId: null,
                status: "",
                reason: "",
              })
            }
          >
            Cancel
          </Button>
          <Button
            // onClick={handleUpdatePostStatus}
            onClick={handleUpdateReportStatus}
            variant="contained"
            style={{ backgroundColor: "#FE5A97", color: "#fff" }}
          >
            Update Status
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FlaggedContentPage;
